import {
  GetPaymentBreakdownResponse,
  BreakdownGuaranteeDto,
  BreakdownTotalByBeneficiaryDto,
  BreakdownFeeDto,
  GuaranteeBeneficiaryDto,
  BreakdownCommissionDto,
  PricingDto,
} from "api/payment-schedule";
import { Shield, FileStack, Building2, Users, Banknote } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { paymentScheduleAPI } from "services/swagger-api";
import { formatAmount, formatIban, InfoCard, InfoRow } from "./PaymentScheduleInfoTab";

// ============= Helpers =============

function formatPercentage(value: number): string {
  return value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " %";
}

const beneficiaryTypeConfig: Record<string, { bg: string; text: string; accent: string }> = {
  Assureur: { bg: "bg-blue-50", text: "text-blue-700", accent: "bg-blue-500" },
  Courtier: { bg: "bg-indigo-50", text: "text-indigo-700", accent: "bg-indigo-500" },
  Gestionnaire: { bg: "bg-purple-50", text: "text-purple-700", accent: "bg-purple-500" },
};

function getBeneficiaryStyle(type?: string | null) {
  if (!type) return { bg: "bg-gray-50", text: "text-gray-700", accent: "bg-gray-400" };
  return beneficiaryTypeConfig[type] ?? { bg: "bg-gray-50", text: "text-gray-700", accent: "bg-gray-400" };
}

const beneficiaryRoleConfig: Record<string, { label: string; dot: string; text: string }> = {
  insurer: { label: "Assureur", dot: "bg-blue-500", text: "text-blue-600" },
  coInsurer: { label: "Co-assureur", dot: "bg-cyan-500", text: "text-cyan-600" },
  reInsurer: { label: "Réassureur", dot: "bg-amber-500", text: "text-amber-600" },
};

// ============= Pricing columns (shared) =============

function PricingCols({ pricing }: { pricing: PricingDto }) {
  return (
    <>
      <td className="px-3 py-2 text-right text-xs text-gray-600 tabular-nums">{formatAmount(pricing.amountExclTax)}</td>
      <td className="px-3 py-2 text-right text-xs text-gray-600 tabular-nums">{formatAmount(pricing.tax)}</td>
      <td className="px-3 py-2 text-right text-xs font-medium text-gray-900 tabular-nums">{formatAmount(pricing.amountInclTax)}</td>
    </>
  );
}

function TableHeader() {
  return (
    <tr className="bg-gray-100/80">
      <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Libellé</th>
      <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide w-24">HT</th>
      <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide w-24">Taxe</th>
      <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wide w-24">TTC</th>
    </tr>
  );
}

// ============= Sub-components =============

function GuaranteeRows({ guarantee }: { guarantee: BreakdownGuaranteeDto }) {
  const commissions = guarantee.commissions ?? [];
  const beneficiaries = guarantee.beneficiaries;

  return (
    <>
      {/* Ligne principale garantie */}
      <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            {beneficiaries?.insurer && (
              <span className="shrink-0 inline-flex px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-secondary/10 text-secondary">{beneficiaries.insurer.designation}</span>
            )}
            <p className="text-sm font-medium text-gray-900">{guarantee.designation}</p>
          </div>
        </td>
        <PricingCols pricing={guarantee.pricing} />
      </tr>

      {/* Répartition */}
      {beneficiaries && (
        <>
          <tr className="border-b border-gray-50 bg-gray-50/30 ">
            <td className="px-3 pl-10 pt-2 pb-1" colSpan={4}>
              <div className="flex items-center gap-1.5">
                <Users size={11} className="text-gray-400" />
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Répartition</span>
              </div>
            </td>
          </tr>
          <BeneficiaryRow beneficiary={beneficiaries.insurer} role="insurer" />
          {beneficiaries.coInsurers.map((b, i) => (
            <BeneficiaryRow key={`co-${b.legalEntityId ?? i}`} beneficiary={b} role="coInsurer" />
          ))}
          {beneficiaries.reInsurers.map((b, i) => (
            <BeneficiaryRow key={`re-${b.legalEntityId ?? i}`} beneficiary={b} role="reInsurer" />
          ))}
        </>
      )}

      {/* Commissions */}
      {commissions.length > 0 && (
        <>
          <tr className="border-b border-gray-50 bg-gray-50/30 ">
            <td className="px-3 pl-10 pt-2 pb-1" colSpan={4}>
              <div className="flex items-center gap-1.5">
                <Banknote size={11} className="text-gray-400" />
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Commissions</span>
              </div>
            </td>
          </tr>
          {commissions.map((c, i) => (
            <CommissionRow key={c.remunerationId ?? i} commission={c} />
          ))}
        </>
      )}
    </>
  );
}

function BeneficiaryRow({ beneficiary, role }: { beneficiary: GuaranteeBeneficiaryDto; role: string }) {
  const config = beneficiaryRoleConfig[role] ?? { label: role, dot: "bg-gray-400", text: "text-gray-600" };
  return (
    <tr className="border-b border-gray-50 bg-gray-50/20 hover:bg-gray-100/50 transition-colors">
      <td className="px-3 py-1.5 pl-10">
        <div className="flex items-center gap-2">
          <span className="shrink-0 inline-flex px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-secondary/10 text-secondary">
            {beneficiary.designation}
          </span>
          <span className={`text-[11px] ${config.text}`}>{config.label}</span>
          <span className="text-[11px] text-gray-400">{formatPercentage(beneficiary.quotaShareRate)}</span>
        </div>
        {beneficiary.bankAccountIban && (
          <p className="text-[11px] text-gray-400 font-mono mt-0.5">{formatIban(beneficiary.bankAccountIban)}</p>
        )}
      </td>
      <PricingCols pricing={beneficiary.pricing} />
    </tr>
  );
}

function CommissionRow({ commission: c }: { commission: BreakdownCommissionDto }) {
  const benef = c.beneficiary;
  return (
    <tr className="border-b border-gray-50 bg-gray-50/20 hover:bg-gray-100/50 transition-colors">
      <td className="px-3 py-1.5 pl-10">
        <div className="flex items-center gap-2">
          {benef && (
            <span className="shrink-0 inline-flex px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-secondary/10 text-secondary">{benef.designation}</span>
          )}
          <p className="text-xs text-gray-700">{c.label}</p>
        </div>
        {benef?.bankAccountIban && (
          <p className="text-[11px] text-gray-400 font-mono mt-0.5 pl-1">{formatIban(benef.bankAccountIban)}</p>
        )}
      </td>
      <PricingCols pricing={c.pricing} />
    </tr>
  );
}

function FeeTable({ fees, totalHT, totalTax, totalTTC }: { fees: BreakdownFeeDto[]; totalHT: number; totalTax: number; totalTTC: number }) {
  return (
    <table className="w-full">
      <thead>
        <TableHeader />
      </thead>
      <tbody>
          {fees.map((fee, i) => {
            const benef = fee.beneficiary;
            const benefStyle = getBeneficiaryStyle(benef?.type);
            return (
              <tr key={`${fee.feeId}-${i}`} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {benef && (
                      <span className="shrink-0 inline-flex px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-secondary/10 text-secondary">{benef.designation}</span>
                    )}
                    <p className="text-xs font-medium text-gray-700">{fee.label}</p>
                  </div>
                  {benef?.bankAccountIban && (
                    <p className="text-[11px] text-gray-400 font-mono mt-0.5 pl-1">{formatIban(benef.bankAccountIban)}</p>
                  )}
                </td>
                <PricingCols pricing={fee.pricing} />
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="bg-gray-100/80">
            <td className="px-3 py-2 text-xs font-semibold text-gray-700">Sous-total frais</td>
            <td className="px-3 py-2 text-right text-xs font-semibold text-gray-700 tabular-nums">{formatAmount(totalHT)}</td>
            <td className="px-3 py-2 text-right text-xs font-semibold text-gray-700 tabular-nums">{formatAmount(totalTax)}</td>
            <td className="px-3 py-2 text-right text-xs font-bold text-gray-900 tabular-nums">{formatAmount(totalTTC)}</td>
          </tr>
        </tfoot>
      </table>
  );
}

function BeneficiaryCard({ total }: { total: BreakdownTotalByBeneficiaryDto }) {
  const style = getBeneficiaryStyle(total.beneficiaryType);
  const fluxList = total.flowDetails ?? [];
  const proportion = total.shareOfTotal;

  return (
    <InfoCard title={total.beneficiary.designation} icon={Building2}>
      <InfoRow label="Type" value={
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
          {total.beneficiaryType}
        </span>
      } />
      <InfoRow label="Montant total" value={<span className="font-bold">{formatAmount(total.totalAmount)}</span>} />
      {total.beneficiary.bankAccountIban && (
        <InfoRow label="IBAN" value={<span className="font-mono text-xs">{formatIban(total.beneficiary.bankAccountIban)}</span>} />
      )}
      <InfoRow label="Part du total" value={formatPercentage(proportion)} />
      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${style.accent} rounded-full transition-all duration-500`} style={{ width: `${proportion}%` }} />
      </div>
      {fluxList.map((flux, i) => (
        <InfoRow key={i} label={flux.label} value={formatAmount(flux.amount)} />
      ))}
    </InfoCard>
  );
}

// ============= Tab Component =============

interface PaymentScheduleBreakdownTabProps {
  paymentScheduleId: string;
}

export const PaymentScheduleBreakdownTab: FC<PaymentScheduleBreakdownTabProps> = ({ paymentScheduleId }) => {
  const [breakdown, setBreakdown] = useState<GetPaymentBreakdownResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBreakdown = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await paymentScheduleAPI.getPaymentBreakdown({ paymentScheduleId });
        setBreakdown(data);
      } catch (err) {
        console.error("Erreur lors du chargement de la répartition:", err);
        setError("Impossible de charger la répartition");
      } finally {
        setIsLoading(false);
      }
    };
    fetchBreakdown();
  }, [paymentScheduleId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <p className="text-gray-500">Chargement de la répartition...</p>
      </div>
    );
  }

  if (error || !breakdown) {
    return (
      <div className="flex items-center justify-center h-48">
        <p className="text-red-500">{error || "Répartition introuvable"}</p>
      </div>
    );
  }

  const guarantees = breakdown.guarantees ?? [];
  const fees = breakdown.fees ?? [];
  const totals = breakdown.totalsByBeneficiary ?? [];

  const { totalGuarantees, totalFees, total } = breakdown;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Colonne gauche (2/3) : Garanties + Frais */}
        <div className="xl:col-span-2 space-y-6 self-start">
          {/* Garanties */}
          {guarantees.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 bg-primary">
                <Shield size={16} className="text-white" />
                <h3 className="text-sm font-semibold text-white">Garanties ({guarantees.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <TableHeader />
                  </thead>
                  <tbody>
                    {guarantees.map((g) => (
                      <GuaranteeRows key={g.guaranteeId} guarantee={g} />
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100/80">
                      <td className="px-3 py-2.5 text-sm font-semibold text-gray-700">Sous-total garanties</td>
                      <td className="px-3 py-2.5 text-right text-sm font-semibold text-gray-700 tabular-nums">{formatAmount(totalGuarantees.amountExclTax)}</td>
                      <td className="px-3 py-2.5 text-right text-sm font-semibold text-gray-700 tabular-nums">{formatAmount(totalGuarantees.tax)}</td>
                      <td className="px-3 py-2.5 text-right text-sm font-bold text-gray-900 tabular-nums">{formatAmount(totalGuarantees.amountInclTax)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Frais */}
          {fees.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 bg-primary">
                <FileStack size={16} className="text-white" />
                <h3 className="text-sm font-semibold text-white">Frais ({fees.length})</h3>
              </div>
              <div className="overflow-x-auto">
                <FeeTable fees={fees} totalHT={totalFees.amountExclTax} totalTax={totalFees.tax} totalTTC={totalFees.amountInclTax} />
              </div>
            </div>
          )}

          {/* Total global */}
          <div className="flex items-center justify-between px-5 py-4 bg-primary rounded-xl text-white">
            <span className="text-sm font-semibold">Total</span>
            <div className="flex items-center gap-8">
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wide text-white/60">HT</p>
                <p className="text-sm font-bold tabular-nums">{formatAmount(total.amountExclTax)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wide text-white/60">Taxe</p>
                <p className="text-sm font-bold tabular-nums">{formatAmount(total.tax)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wide text-white/60">TTC</p>
                <p className="text-lg font-bold tabular-nums">{formatAmount(total.amountInclTax)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Colonne droite (1/3) : Totaux par bénéficiaire */}
        {totals.length > 0 && (
          <div className="space-y-4 self-start">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide px-1">Répartition par bénéficiaire</h3>
            {totals.map((total, i) => (
              <BeneficiaryCard key={i} total={total} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
